import axios from 'axios';

const RETRY_TEXT = 'Please try again.';

const DEFAULT_MESSAGES = {
  validation: 'Some information is invalid. Please check the required fields and try again.',
  unauthorized: 'Your session has expired. Please log in again.',
  forbidden: 'You do not have permission to perform this action.',
  network: 'We could not connect to the server. Please check your internet connection and try again.',
  server: 'A server error occurred. Please try again in a moment.',
  default: 'We could not process your request. Please try again.',
};

const RETRYABLE_TYPES = new Set(['network', 'server', 'default']);

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function collectMessages(value) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectMessages(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).flatMap((item) => collectMessages(item));
  }

  return [];
}

function firstMessage(value) {
  return collectMessages(value)[0] || '';
}

export function toUserMessage(value, fallbackMessage = DEFAULT_MESSAGES.default) {
  return firstMessage(value) || fallbackMessage;
}

export function normalizeValidationErrors(errors) {
  if (!isPlainObject(errors)) {
    return {};
  }

  return Object.entries(errors).reduce((normalized, [field, value]) => {
    const messages = collectMessages(value);
    if (messages.length > 0) {
      normalized[field] = messages;
    }
    return normalized;
  }, {});
}

function firstValidationMessage(errors) {
  return firstMessage(errors);
}

function backendMessage(data) {
  if (!isPlainObject(data)) {
    return firstMessage(data);
  }

  return firstMessage(data.message) || firstValidationMessage(data.errors || data);
}

function withRetryGuidance(message, type) {
  const cleanMessage = String(message || DEFAULT_MESSAGES.default).trim();

  if (!RETRYABLE_TYPES.has(type) || cleanMessage.toLowerCase().includes('try again')) {
    return cleanMessage;
  }

  return `${cleanMessage} ${RETRY_TEXT}`;
}

export function normalizeApiError(error, fallbackMessage = DEFAULT_MESSAGES.default) {
  const status = Number(error?.response?.status || 0);
  const data = error?.response?.data;
  const backendText = backendMessage(data);

  if (!status) {
    return {
      type: 'network',
      status: 0,
      message: DEFAULT_MESSAGES.network,
      canRetry: true,
      validationErrors: {},
    };
  }

  if (status === 401) {
    return {
      type: 'unauthorized',
      status,
      message: backendText || DEFAULT_MESSAGES.unauthorized,
      canRetry: false,
      validationErrors: {},
    };
  }

  if (status === 403) {
    return {
      type: 'forbidden',
      status,
      message: backendText || DEFAULT_MESSAGES.forbidden,
      canRetry: false,
      validationErrors: {},
    };
  }

  if (status === 422) {
    const validationErrors = normalizeValidationErrors(isPlainObject(data) ? data.errors || data : null);
    const validationText = firstValidationMessage(validationErrors);
    return {
      type: 'validation',
      status,
      message: validationText || backendText || DEFAULT_MESSAGES.validation,
      canRetry: false,
      validationErrors,
    };
  }

  if (status >= 500) {
    return {
      type: 'server',
      status,
      message: withRetryGuidance(backendText || DEFAULT_MESSAGES.server, 'server'),
      canRetry: true,
      validationErrors: {},
    };
  }

  return {
    type: 'default',
    status,
    message: withRetryGuidance(backendText || fallbackMessage || DEFAULT_MESSAGES.default, 'default'),
    canRetry: true,
    validationErrors: {},
  };
}

export function getApiErrorMessage(error, fallbackMessage) {
  return error?.apiError?.message || normalizeApiError(error, fallbackMessage).message;
}

export function normalizeFetchError(response, data, fallbackMessage) {
  return normalizeApiError({ response: { status: response?.status || 0, data } }, fallbackMessage);
}

export function installApiErrorHandling() {
  if (axios.__cheecoApiErrorHandlingInstalled) {
    return;
  }

  axios.__cheecoApiErrorHandlingInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const normalized = normalizeApiError(error);
      error.apiError = normalized;

      if (error.response) {
        const currentData = isPlainObject(error.response.data) ? error.response.data : {};
        const hasBackendMessage = typeof currentData.message === 'string' && currentData.message.trim();
        const shouldApplyMessage = normalized.type !== 'default' || hasBackendMessage;
        error.response.data = {
          ...currentData,
          ...(shouldApplyMessage ? { message: normalized.message } : {}),
          errors: normalized.validationErrors,
          error_type: normalized.type,
          can_retry: normalized.canRetry,
        };
      } else {
        error.response = {
          status: 0,
          data: {
            message: normalized.message,
            errors: {},
            error_type: normalized.type,
            can_retry: true,
          },
        };
      }

      return Promise.reject(error);
    }
  );
}
